import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, queryOptions } from "@tanstack/react-query";
import { absoluteUrl } from "@/lib/site";
import { getLicenseTemplates } from "@/lib/bkls.functions";
import { beatsTiersQuery } from "./beats.index";
import { UNIVERSAL_RESTRICTIONS, formatMoney, tierPriceFor } from "@/lib/bkls";

const templatesQuery = queryOptions({
  queryKey: ["bkls", "templates"],
  queryFn: () => getLicenseTemplates(),
});

export const Route = createFileRoute("/beats/license")({
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(beatsTiersQuery),
      context.queryClient.ensureQueryData(templatesQuery),
    ]);
    return null;
  },
  head: () => ({
    meta: [
      { title: "Licence Terms & FAQ · Beats · Buddha KA" },
      {
        name: "description",
        content:
          "What a Buddha KA beat licence grants and prohibits: tiers, limits, territory, term, credit, restrictions and the full versioned agreement text.",
      },
      { property: "og:title", content: "Licence Terms & FAQ · Buddha KA Beats" },
      { property: "og:description", content: "Tiers, limits, restrictions and the full agreement text." },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/beats/license") }],
  }),
  component: LicenseInfo,
});

const FAQ: Array<{ q: string; a: string }> = [
  {
    q: "Do I own the beat after buying a licence?",
    a: "No. Every transaction grants a licence to use the recording within stated limits. Copyright in the composition and master stays with Buddha KA. No tier transfers ownership.",
  },
  {
    q: "Can I register the song with Content ID or a fingerprinting service?",
    a: "Not without prior written permission. Registering a licensed beat with Content ID or any digital fingerprint system would claim rights you do not hold, and it blocks other licensees.",
  },
  {
    q: "Can I resell, share or sublicense the files?",
    a: "No. The licence is personal to the licensee. Resale, sublicensing and redistribution of the raw beat or stems are prohibited under every tier.",
  },
  {
    q: "What happens if the tier wording or price changes later?",
    a: "Nothing changes for you. Each licence stores an immutable snapshot of the exact terms, pricing and template version at purchase time. Later edits never apply retroactively.",
  },
  {
    q: "What if I need terms no tier covers?",
    a: "Use the custom licensing request. Film, sync, brand campaigns, exclusive use and unusual territories are handled case by case with written terms.",
  },
  {
    q: "How do I get my files?",
    a: "Licensed files are private. Your Beats account issues short-lived signed download links for exactly the formats your tier covers, and each download is logged.",
  },
];

function LicenseInfo() {
  const tiers = useQuery(beatsTiersQuery);
  const templates = useQuery(templatesQuery);

  return (
    <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-24">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Licensing</p>
        <h1 className="mt-4 font-serif text-5xl">What a licence grants.</h1>
        <p className="mt-5 text-muted-foreground">
          Plain terms, permanent records. Read this before licensing anything.
        </p>
      </header>

      <section className="mt-14" aria-labelledby="tiers">
        <h2 id="tiers" className="font-serif text-3xl text-gold-luminous">
          Tiers and limits
        </h2>
        <div className="mt-6 space-y-4">
          {(tiers.data ?? []).map((t) => {
            const price = tierPriceFor(t);
            return (
              <article key={t.key} className="rounded-2xl border hairline bg-charcoal p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="font-serif text-2xl text-ivory">{t.name}</h3>
                  <p className="text-sm text-gold">
                    {t.is_quote_only ? "By quote" : price ? formatMoney(price, t.currency) : "Price on request"}
                  </p>
                </div>
                {t.tagline && <p className="mt-2 text-sm text-muted-foreground">{t.tagline}</p>}
                <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
                  {[
                    ["Formats", t.file_formats.join(", ") || "—"],
                    ["Streams", t.stream_limit],
                    ["Video", t.video_limit],
                    ["Distribution", t.distribution_limit],
                    ["Monetization", t.monetization],
                    ["Performance rights", t.performance_rights],
                    ["Territory", t.territory],
                    ["Term", t.term],
                    ["Credit", t.credit_required],
                  ]
                    .filter(([, v]) => Boolean(v))
                    .map(([k, v]) => (
                      <div key={k as string}>
                        <dt className="text-[10px] uppercase tracking-[0.2em] text-gold/80">{k}</dt>
                        <dd className="mt-1 text-muted-foreground">{v}</dd>
                      </div>
                    ))}
                </dl>
                {t.restrictions.length > 0 && (
                  <ul className="mt-5 space-y-1 text-xs text-muted-foreground">
                    {t.restrictions.map((r) => (
                      <li key={r} className="pl-4 -indent-4">
                        — {r}
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section className="mt-14" aria-labelledby="always">
        <h2 id="always" className="font-serif text-3xl text-gold-luminous">
          Always true, every tier
        </h2>
        <ul className="mt-5 space-y-2 text-sm text-muted-foreground">
          {UNIVERSAL_RESTRICTIONS.map((r) => (
            <li key={r} className="pl-4 -indent-4">
              — {r}
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-14" aria-labelledby="faq">
        <h2 id="faq" className="font-serif text-3xl text-gold-luminous">
          FAQ
        </h2>
        <div className="mt-6 space-y-4">
          {FAQ.map((item) => (
            <details key={item.q} className="rounded-2xl border hairline bg-charcoal p-5">
              <summary className="cursor-pointer font-serif text-lg text-ivory">{item.q}</summary>
              <p className="mt-3 text-sm text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mt-14" aria-labelledby="agreements">
        <h2 id="agreements" className="font-serif text-3xl text-gold-luminous">
          Agreement text
        </h2>
        <p className="mt-3 text-sm text-muted-foreground">
          Templates are versioned. The version in force when you purchase is snapshotted into your licence and
          never changes.
        </p>
        <div className="mt-6 space-y-4">
          {(templates.data ?? []).map((t) => (
            <details key={`${t.tier_key}-${t.version}`} className="rounded-2xl border hairline bg-charcoal p-5">
              <summary className="cursor-pointer font-serif text-lg text-ivory">
                {t.title} <span className="text-xs text-gold">v{t.version}</span>
              </summary>
              <pre className="mt-4 whitespace-pre-wrap font-sans text-sm leading-relaxed text-muted-foreground">
                {t.body_md}
              </pre>
            </details>
          ))}
        </div>
      </section>

      <div className="mt-14 flex flex-wrap gap-3">
        <Link
          to="/beats/catalog"
          className="rounded-full border border-gold px-5 py-2.5 text-sm text-gold-luminous transition-all hover:bg-gold hover:text-obsidian"
        >
          Browse the catalog
        </Link>
        <Link
          to="/beats/custom"
          className="rounded-full border hairline px-5 py-2.5 text-sm text-foreground/80 hover:border-gold hover:text-gold"
        >
          Request a custom licence
        </Link>
      </div>
    </div>
  );
}
